import type { QueryClient } from '@tanstack/react-query';
import { ApiError, api, fetchApi } from '@/lib/api';
import { createExtensionApi } from '@/lib/extension-api';
import type { Notification, NotificationScope, NotificationType } from './types';

// Cache keys (must mirror hooks.ts). Kept in sync manually — the hook file
// is private for historical reasons; if this duplicates grow, promote these
// to a shared `keys.ts` module.
const NOTIFICATIONS_KEY = ['notifications'] as const;
function itemNotificationsKey(itemUin: number) {
  return [...NOTIFICATIONS_KEY, 'item', itemUin] as const;
}
function itemMapKey() {
  return [...NOTIFICATIONS_KEY, 'item-map'] as const;
}
function tagsNotificationKey(tags: number[], brandIds?: number[] | null) {
  return [
    ...NOTIFICATIONS_KEY,
    'tags',
    [...tags].sort((a, b) => a - b),
    brandIds ? [...brandIds].sort((a, b) => a - b) : null,
  ] as const;
}

function invalidateFor(queryClient: QueryClient, notification: Notification) {
  queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  queryClient.invalidateQueries({ queryKey: itemMapKey() });
  if (notification.itemUin) {
    queryClient.invalidateQueries({ queryKey: itemNotificationsKey(notification.itemUin) });
  }
  if (notification.tags && notification.tags.length > 0) {
    queryClient.invalidateQueries({
      queryKey: tagsNotificationKey(notification.tags, notification.brandIds ?? null),
    });
  }
}

export interface CreateNotificationBody {
  scope: NotificationScope;
  type: NotificationType;
  itemUin?: number;
  tags?: number[];
  brandIds?: number[];
  priceThreshold?: number;
  expiresAt?: string;
}

export interface UpdateNotificationBody {
  type?: NotificationType;
  priceThreshold?: number;
  expiresAt?: string | null;
  active?: boolean;
}

/**
 * Minimal data-access interface for notification CRUD. Has two implementations:
 *   - `cookieNotificationsClient`                      — used by the main web app.
 *   - `createExtensionNotificationsClient(getToken)`   — used by the bearer-auth'd
 *     embed route `/embed/notification-modal/$uin` (mounted as the tracker
 *     modal iframe by the extension content script).
 *
 * Keep this surface small. The /item-map, /item-map-scoped, /tags, and
 * /channels endpoints are not part of this interface — they're scenario-specific
 * and live in their own data modules.
 */
export interface NotificationsDataClient {
  getForItem(itemUin: number): Promise<Notification | null>;
  create(body: CreateNotificationBody): Promise<Notification>;
  update(id: string, body: UpdateNotificationBody): Promise<Notification>;
  remove(id: string): Promise<void>;
}

/**
 * Cookie-auth implementation. Reuses the existing `fetchApi` wrapper which
 * unwraps Eden Treaty's `{ data, error }` envelope AND our API's inner
 * `{ success, data }` envelope, throwing `ApiError` on non-2xx or error bodies.
 *
 * NOTE: This raw client performs NO React Query cache invalidation. Callers
 * that live inside the React tree should prefer `createCookieNotificationsClient(queryClient)`
 * so that other components observing `useItemNotificationMap()`,
 * `useItemNotification()`, etc. refresh after mutations.
 */
export const cookieNotificationsClient: NotificationsDataClient = {
  async getForItem(itemUin) {
    const data = await fetchApi(api.api.notifications.item({ itemUin: String(itemUin) }).get());
    const notifications = data as Notification[];
    return notifications[0] ?? null;
  },
  async create(body) {
    const data = await fetchApi(api.api.notifications.post(body));
    return data as Notification;
  },
  async update(id, body) {
    const data = await fetchApi(api.api.notifications({ id }).put(body));
    return data as Notification;
  },
  async remove(id) {
    await fetchApi(api.api.notifications({ id }).delete());
  },
};

/**
 * Cookie-auth implementation with React Query cache invalidation baked in.
 * Drop-in replacement for the raw `cookieNotificationsClient` when called
 * from components that expect mutations to refresh other views (e.g. the
 * dashboard notification-map, listing-page bells).
 *
 * Mirrors the invalidation set of `useCreateNotification` /
 * `useUpdateNotification` / `useDeleteNotification` in `hooks.ts`.
 */
export function createCookieNotificationsClient(queryClient: QueryClient): NotificationsDataClient {
  return {
    getForItem: cookieNotificationsClient.getForItem,
    async create(body) {
      const data = await fetchApi(api.api.notifications.post(body));
      const notification = data as Notification;
      invalidateFor(queryClient, notification);
      return notification;
    },
    async update(id, body) {
      const data = await fetchApi(api.api.notifications({ id }).put(body));
      const notification = data as Notification;
      invalidateFor(queryClient, notification);
      return notification;
    },
    async remove(id) {
      // `remove` doesn't return the deleted row, so we take the broader
      // approach of invalidating everything under the notifications root.
      // This is the same blast radius useDeleteNotification uses when it
      // lacks item/tag context.
      await fetchApi(api.api.notifications({ id }).delete());
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: itemMapKey() });
    },
  };
}

/**
 * Bearer-auth implementation. Call sites pass a `getToken` getter so the token
 * can be refreshed between calls without rebuilding the client (token TTL is
 * 15 min; the extension's content-script postMessage bridge supplies a fresh
 * token via `useExtensionAuth`).
 *
 * We intentionally reuse `fetchApi` here: the extension Eden client returns
 * the same `{ data, error }` shape and the server emits the same
 * `{ success, data }` envelope, so the unwrap + error-throwing behavior is
 * identical to the cookie path.
 */
export function createExtensionNotificationsClient(
  getToken: () => string | null,
): NotificationsDataClient {
  const extApi = createExtensionApi(getToken);
  return {
    async getForItem(itemUin) {
      const data = await fetchApi(
        extApi.api.notifications.item({ itemUin: String(itemUin) }).get(),
      );
      const notifications = data as Notification[];
      return notifications[0] ?? null;
    },
    async create(body) {
      const data = await fetchApi(extApi.api.notifications.post(body));
      return data as Notification;
    },
    async update(id, body) {
      const data = await fetchApi(extApi.api.notifications({ id }).put(body));
      return data as Notification;
    },
    async remove(id) {
      await fetchApi(extApi.api.notifications({ id }).delete());
    },
  };
}

// Re-export `ApiError` so callers can narrow on auth failures (e.g. 401 →
// reportAuthExpired) without importing from two modules.
export { ApiError };
